import { useState } from "react";
import { toast } from "sonner";
import { Pause, Play, RotateCcw, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Page } from "../components/Layout";
import { api, notifyErr, fmtDate } from "../lib/api";
import { usePoll } from "../lib/hooks";
import { JobStatusBadge, Tone } from "../lib/labels";

const TYPE = { acquire: "Acquisizione", analyze: "Analisi AI" };
const STAGE = { acquire: "Acquisizione", transcribe: "Trascrizione", classify: "Classificazione", done: "Completato" };

function JobDetail({ id, onClose }) {
  const [j, setJ] = useState(null);
  usePoll(() => id && api.get(`/jobs/${id}`).then((r) => setJ(r.data)), 4000, [id]);
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto bg-white" data-testid="job-detail-drawer">
        <SheetHeader><SheetTitle>Dettaglio job {id?.slice(0, 8)}</SheetTitle></SheetHeader>
        {j && <Table className="mt-4">
          <TableHeader><TableRow><TableHead>Registrazione</TableHead><TableHead>Fase</TableHead><TableHead>Stato</TableHead><TableHead>Tentativi</TableHead><TableHead>Metriche</TableHead><TableHead>Errore</TableHead></TableRow></TableHeader>
          <TableBody>{j.items.map((i) => (
            <TableRow key={i.id} className="text-xs" data-testid={`job-item-${i.id}`}>
              <TableCell>{i.recording.original_name || i.recording.vicidial_recording_id}<div className="text-neutral-500">{i.recording.phone_number}</div></TableCell>
              <TableCell>{STAGE[i.stage]}</TableCell>
              <TableCell><Tone tone={{ done: "green", error: "red", running: "blue", skipped: "gray" }[i.status]}>{i.status}</Tone></TableCell>
              <TableCell>{i.attempts}</TableCell>
              <TableCell className="font-mono text-[10px]">{Object.entries(i.metrics || {}).map(([k, v]) => <div key={k}>{k}: {String(v)}</div>)}</TableCell>
              <TableCell className="text-red-700 max-w-xs">{i.error}</TableCell>
            </TableRow>))}
          </TableBody>
        </Table>}
      </SheetContent>
    </Sheet>
  );
}

export default function Elaborazioni() {
  const [jobs, setJobs] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  usePoll(load, 3000);
  const act = async (id, a) => { try { await api.post(`/jobs/${id}/${a}`); toast.success("Operazione eseguita"); load(); } catch (e) { notifyErr(e); } };
  return (
    <Page title="Elaborazioni" subtitle="Coda dei job di acquisizione e analisi AI locale (faster-whisper + Ollama). Aggiornamento automatico ogni 3 secondi." testid="elaborazioni-page">
      <div className="bg-white border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Job</TableHead><TableHead>Cliente</TableHead><TableHead>Tipo</TableHead><TableHead>Stato</TableHead><TableHead className="w-64">Avanzamento</TableHead><TableHead>Creato</TableHead><TableHead>Fine</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {jobs.map((j) => {
              const pct = j.total ? Math.round(((j.done + j.failed) / j.total) * 100) : 0;
              return (
                <TableRow key={j.id} className="row-hover text-sm cursor-pointer" onClick={() => setSel(j.id)} data-testid={`job-row-${j.id}`}>
                  <TableCell className="font-mono text-xs">{j.id.slice(0, 8)}</TableCell>
                  <TableCell>{j.customer_name}</TableCell>
                  <TableCell>{TYPE[j.type] || j.type}{j.params?.auto_analyze ? " + Analisi" : ""}</TableCell>
                  <TableCell><JobStatusBadge status={j.status} testid={`job-status-${j.id}`} /></TableCell>
                  <TableCell>
                    <Progress value={pct} className="h-1.5 rounded-none" />
                    <div className="text-[11px] text-neutral-500 mt-1 tabular-nums" data-testid={`job-progress-${j.id}`}>{j.done}/{j.total} completati · {j.failed} errori</div>
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(j.created_at)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(j.finished_at)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
                    {["queued", "running"].includes(j.status) && <Button size="sm" variant="ghost" className="h-7" title="Pausa" onClick={() => act(j.id, "pause")} data-testid={`job-pause-${j.id}`}><Pause size={14} /></Button>}
                    {j.status === "paused" && <Button size="sm" variant="ghost" className="h-7" title="Riprendi" onClick={() => act(j.id, "resume")} data-testid={`job-resume-${j.id}`}><Play size={14} /></Button>}
                    {(j.failed > 0 || (j.status === "cancelled" && j.done < j.total)) && <Button size="sm" variant="ghost" className="h-7" title="Riprova errori" onClick={() => act(j.id, "retry")} data-testid={`job-retry-${j.id}`}><RotateCcw size={14} /></Button>}
                    {["queued", "running", "paused"].includes(j.status) && <Button size="sm" variant="ghost" className="h-7 text-red-600" title="Annulla" onClick={() => act(j.id, "cancel")} data-testid={`job-cancel-${j.id}`}><X size={14} /></Button>}
                  </TableCell>
                </TableRow>
              );
            })}
            {!jobs.length && <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-10">Nessun job.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <JobDetail id={sel} onClose={() => setSel(null)} />
    </Page>
  );
}
